import { ChartPoint } from '../report-aggregation';

/**
 * How an ordered answer list divides into named groups: "Promoters" are the top three options,
 * "Passives" the next two, "Detractors" the bottom five.
 *
 * Stored as a run length per band rather than as a list of answer texts, for three reasons. A
 * template re-bound to another survey's question keeps working -- `variableSlots` exists so a deck
 * can be pointed at a different study, and answer *wording* does not survive that where "three
 * options in from this end" does. It composes with `netExclude`, which already filters the scale by
 * label before anything walks it. And it is the shape `StageMatrixExtraData.stages` already
 * uses for the same idea, so there is one concept here and not two.
 *
 * Deliberately a count rather than a cut at a percentage, for the reason `tiered-ranking` records
 * about its tiers: this deck has a live filter rail, and a threshold would move answers between
 * bands every time a client filters, so a band would mean something different in every view of the
 * same report. Option *positions* do not move.
 */
export interface BandSpec {
  label: string;
  /** How many consecutive options this band covers, reading in the order the survey asked them. */
  options: number;
}

export interface Band {
  label: string;
  /** The options it covers, in asked order. */
  points: ChartPoint[];
  /** Their shares, summed. */
  share: number;
  /** The answers at its ends, as the survey worded them, e.g. "8-10" or "Getting better". */
  range: string;
}

/**
 * The real answers of a question, in asked order, with anything that is not on the scale removed.
 *
 * `optionIndex < 0` is the synthetic "Not asked" point a compare-by column adds; `exclude` is for
 * answers that are genuinely off the scale -- "Not sure", "Prefer not to say". Both take up their
 * share of the sample, and neither is the top or the bottom of anything.
 */
export function bandScale(points: ChartPoint[], exclude: string[] = []): ChartPoint[] {
  const off = new Set(exclude);
  return points.filter(point => point.optionIndex >= 0 && !off.has(point.label));
}

/**
 * Walks the bands across the scale, left to right.
 *
 * **Null rather than a partial answer** when the bands claim more options than the scale has. The
 * ladder truncates in the same situation and is right to: a stage drawn one column short is still a
 * readable drawing. A net computed over a scale that is not the one the bands were written for is a
 * wrong *number*, and a wrong number published to a client is not a degraded version of a right one.
 *
 * Options past the last band are left unbanded on purpose -- they stay in the base, and they belong
 * to neither end of a net. That is how `netExclude` already behaves, and how the ladder treats a
 * rung no stage claims.
 *
 * Walks by array position, never by the value of `optionIndex`: for codes absent from the datamap
 * that number is assigned by discovery order and is not comparable between one record set and
 * another, which is exactly what a compare-by breakout produces.
 */
export function bandsOf(scale: ChartPoint[], spec: BandSpec[] | undefined): Band[] | null {
  if (!spec?.length) return null;

  const sizes = spec.map(band => Math.max(0, Math.floor(band.options)));
  if (sizes.some(size => size < 1)) return null;
  if (sizes.reduce((total, size) => total + size, 0) > scale.length) return null;

  // Shortened once across every answer the bands cover, before any band is cut out of it: a scale
  // written as full sentences repeats its stem in all of them, and that stem is the same noise in
  // every band. Answers no band claims are left out of the comparison -- a trailing "Not sure"
  // shares nothing with the rest and would leave nothing to strip.
  const claimed = sizes.reduce((total, size) => total + size, 0);
  const short = trimShared(scale.slice(0, claimed).map(point => point.label.trim()));

  let at = 0;
  return spec.map((band, index) => {
    const points = scale.slice(at, at + sizes[index]);
    const range = rangeOf(short.slice(at, at + sizes[index]));
    at += sizes[index];
    return {
      label: band.label,
      points,
      share: points.reduce((total, point) => total + point.percent, 0),
      range,
    };
  });
}

/**
 * The favourable band minus the unfavourable one, the middle ignored.
 *
 * The same definition `stacked-scale` computes for its net column, with the one difference
 * this exists for: the two ends can be different widths. A ten-point advocacy scale nets three
 * options against five, which a single symmetric count cannot say.
 *
 * `topFirst` names the end the *favourable* answers sit at, because a survey lists its scale either
 * way round and the datamap does not record which. Read the wrong way this does not degrade, it
 * inverts: +61 publishes as -61. Whatever calls this has to make the choice visible to whoever set
 * it -- see the dial's derived footnote, which prints the bands it actually summed.
 */
export function netOfBands(bands: Band[], topFirst: boolean): number | null {
  if (bands.length < 2) return null;
  const top = topFirst ? bands[0] : bands[bands.length - 1];
  const bottom = topFirst ? bands[bands.length - 1] : bands[0];
  return top.share - bottom.share;
}

/** The band a reader would call the good end, for a shares line that reads best-first. */
export function bandsBestFirst(bands: Band[], topFirst: boolean): Band[] {
  return topFirst ? bands : [...bands].reverse();
}

/**
 * One band's share, picked best-first: 0 is the favourable end, whichever end of the asked order
 * that is. Clamped rather than null, so a band removed from under a saved choice falls back to the
 * nearest one that still exists.
 */
export function shareOfBand(bands: Band[], topFirst: boolean, index = 0): Band | null {
  if (!bands.length) return null;
  const ordered = bandsBestFirst(bands, topFirst);
  return ordered[Math.min(Math.max(0, Math.floor(index)), ordered.length - 1)];
}

/**
 * The bands an older block meant by a symmetric box count: `boxes` at each end and whatever lies
 * between as the middle. Kept so a design written before bands existed -- "top-2 minus bottom-2" --
 * still computes the figure it always did, from the one implementation every banded module shares.
 *
 * Labelled from the end they sit at, so the headline a share draws reads "Top-2 box" either way
 * round. Undefined where the two ends would overlap, which is the case the old count refused too.
 */
export function bandsFromBoxes(scaleLength: number, boxes: number, topFirst: boolean): BandSpec[] | undefined {
  const size = Math.max(1, Math.floor(boxes));
  if (scaleLength < size * 2) return undefined;

  const name = (end: 'Top' | 'Bottom') => size === 1 ? `${end} box` : `${end}-${size} box`;
  const middle = scaleLength - size * 2;
  const first = name(topFirst ? 'Top' : 'Bottom');
  const last = name(topFirst ? 'Bottom' : 'Top');
  return [
    { label: first, options: size },
    ...(middle ? [{ label: 'Middle', options: middle }] : []),
    { label: last, options: size },
  ];
}

/**
 * What the band covers, in the survey's own words: "8-10" on a numeric scale, "Getting better" where
 * it is one answer. Printed beside the figure so the card shows its own working -- the only real
 * defence against a band read from the wrong end.
 *
 * Trimmed a second time against the band's own two ends, because what they still share after the
 * scale-wide pass is the part this band has in common with itself: "be much higher than" against
 * "be somewhat higher than" is "much" against "somewhat".
 */
function rangeOf(labels: string[]): string {
  if (!labels.length) return '';
  const first = labels[0];
  const last = labels[labels.length - 1];
  if (first === last) return first;
  const [from, to] = trimShared([first, last]);
  return `${from}-${to}`;
}

/**
 * The same labels with the whole words they all open and close with removed.
 *
 * Prefix and suffix only, never a word that merely appears in several of them: an answer list is
 * written as variations on a stem -- "Electricity demand will be **much higher** than today" -- and
 * what carries the meaning is what sits between the parts that repeat. Pulling out words from the
 * middle would leave "much somewhat" style wreckage that reads as an answer nobody gave.
 *
 * Returns the labels untouched where the trim would leave any of them empty. "Strongly agree"
 * against "Agree" shares its entire tail, and "Strongly" against nothing is not a range.
 */
function trimShared(labels: string[]): string[] {
  if (labels.length < 2) return labels;

  const lists = labels.map(label => label.split(/\s+/).filter(Boolean));
  const shortest = Math.min(...lists.map(list => list.length));
  const same = (a: string, b: string) => key(a) === key(b) && !!key(a);

  let head = 0;
  while (head < shortest && lists.every(list => same(list[head], lists[0][head]))) head++;

  let tail = 0;
  while (head + tail < shortest
    && lists.every(list => same(list[list.length - 1 - tail], lists[0][lists[0].length - 1 - tail]))) {
    tail++;
  }

  if (!head && !tail) return labels;
  const trimmed = lists.map(list => list.slice(head, list.length - tail));
  return trimmed.some(list => !list.length) ? labels : trimmed.map(list => list.join(' '));
}

/** Words match on their letters: punctuation and case are not what makes two answers differ. */
function key(word: string): string {
  return (word ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
}
